import React from "react";
import { Box, Typography } from "../../../design-system";
import { CloudUploadIcon } from "../../../design-system/icons";
import movFile from "../../../../assets/illustrations/mov-file.png";
import logoSite from "../../../../assets/brand/logo_site.svg";

/**
 * Looping hero animation = a MINI version of the real upload box, telling the whole
 * CherryClipz story as motion:
 *   1) a cursor drags & drops a video file into the (mini) upload area,
 *   2) a pink progress bar fills — uploading / processing,
 *   3) the list slowly expands and the clips generate one-by-one,
 *   4) the cursor clicks two clips (each highlights red, like the ET) then clicks Export.
 * Pure CSS keyframes, no video assets, fixed height (no page jump), loops forever.
 */

const CYCLE = "9s"; // one loop; all timings below are % of this so they stay in sync.

const ROWS = [
  { speaker: "Speaker A", emo: "+", text: "This is the moment everything changed.", base: ["#2c2533", "#181420"], selectAt: 68 },
  { speaker: "Speaker B", emo: "=", text: "Let me show you exactly how it works.", base: ["#2a2a37", "#15151f"], selectAt: 72 },
  { speaker: "Speaker A", emo: "-", text: "I almost gave up right here.", base: ["#34252f", "#1b141a"], selectAt: 76 },
];

const EMO_COLOR: Record<string, string> = { "+": "#36c275", "=": "#8d99b7", "-": "#e64545" };

// Clips generate WHILE the red progress bar is filling (20% -> 60%).
const APPEAR = [32, 42, 52];
const rowKeyframes = (appear: number) => ({
  "0%": { opacity: 0, transform: "translateY(-12px) scale(0.88)" },
  [`${appear - 6}%`]: { opacity: 0, transform: "translateY(-12px) scale(0.88)" },
  [`${appear}%`]: { opacity: 1, transform: "translateY(0) scale(1)" },
  "100%": { opacity: 1, transform: "translateY(0) scale(1)" },
});

// Selected rows highlight red when the cursor clicks them.
const selectKeyframes = (at: number) => ({
  // snaps on instantly at the click (tiny 0.2% ramp), then holds.
  "0%": { backgroundColor: "rgba(230,29,80,0)", borderLeftColor: "rgba(230,29,80,0)" },
  [`${at - 0.2}%`]: { backgroundColor: "rgba(230,29,80,0)", borderLeftColor: "rgba(230,29,80,0)" },
  [`${at}%`]: { backgroundColor: "rgba(230,29,80,0.22)", borderLeftColor: "#e61d50" },
  "100%": { backgroundColor: "rgba(230,29,80,0.22)", borderLeftColor: "#e61d50" },
});

// Checkbox ticks ON (pink + check) the instant the cursor clicks the clip — stays checked.
const checkBoxKeyframes = (at: number) => ({
  "0%": { backgroundColor: "transparent", borderColor: "rgba(255,255,255,0.3)" },
  [`${at - 0.2}%`]: { backgroundColor: "transparent", borderColor: "rgba(255,255,255,0.3)" },
  [`${at}%`]: { backgroundColor: "#e61d50", borderColor: "#e61d50" },
  "100%": { backgroundColor: "#e61d50", borderColor: "#e61d50" },
});
const checkMarkKeyframes = (at: number) => ({
  "0%": { opacity: 0, transform: "scale(0.4)" },
  [`${at - 0.2}%`]: { opacity: 0, transform: "scale(0.4)" },
  [`${at}%`]: { opacity: 1, transform: "scale(1)" },
  "100%": { opacity: 1, transform: "scale(1)" },
});

const ThumbScene: React.FC<{ base: [string, string] }> = ({ base }) => (
  <Box
    sx={{
      position: "relative",
      width: { xs: "76px", md: "96px" },
      aspectRatio: "16 / 9",
      borderRadius: "5px",
      overflow: "hidden",
      flexShrink: 0,
      background: `linear-gradient(160deg, ${base[0]}, ${base[1]})`,
      boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
    }}
  >
    {/* CherryClipz play-button logo centered, like a video thumbnail */}
    <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <img src={logoSite} alt="" style={{ height: "46%", width: "auto", opacity: 0.95, display: "block" }} />
    </Box>
    <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 0, height: { xs: "6px", md: "8px" }, background: "rgba(230,29,80,0.45)" }} />
  </Box>
);

const HeroClipDemo: React.FC = () => (
  <Box
    aria-hidden
    sx={{
      width: "100%",
      maxWidth: "440px",
      mx: "auto",
      // FIXED height: list expands within reserved space, so the card never resizes
      // and the page never jumps as the loop expands/collapses.
      height: { xs: "352px", md: "404px" },
      borderRadius: "14px",
      border: "1px solid rgba(230,29,80,0.16)",
      background: "linear-gradient(180deg, rgba(22,18,24,0.95), rgba(13,12,16,0.95))",
      p: { xs: "14px", md: "16px" },
      position: "relative",
      overflow: "hidden",
    }}
  >
    {/* ── Mini upload box (echoes the real one above) ──────────── */}
    <Box
      sx={{
        position: "relative",
        borderRadius: "10px",
        border: "1.5px dashed rgba(141,153,183,0.4)",
        p: "12px 10px 10px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "7px",
        // border turns red on drop and STAYS red through processing + clip generation
        animation: `zonePulse ${CYCLE} ease-in-out infinite`,
        "@keyframes zonePulse": {
          "0%, 9%": { borderColor: "rgba(141,153,183,0.4)", boxShadow: "0 0 0 0 rgba(230,29,80,0)" },
          "13%": { borderColor: "rgba(230,29,80,0.85)", boxShadow: "0 0 0 5px rgba(230,29,80,0.16)" },
          "18%": { borderColor: "rgba(230,29,80,0.85)", boxShadow: "0 0 0 0 rgba(230,29,80,0)" },
          "95%": { borderColor: "rgba(230,29,80,0.85)", boxShadow: "0 0 0 0 rgba(230,29,80,0)" },
          "100%": { borderColor: "rgba(141,153,183,0.4)", boxShadow: "0 0 0 0 rgba(230,29,80,0)" },
        },
      }}
    >
      <CloudUploadIcon size={20} color="#ffffff" />
      <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "10.5px", fontWeight: 600, color: "#e8eaf3", textAlign: "center" }}>
        Drop a file, upload, or paste a link
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", width: "100%", justifyContent: "center" }}>
        <Box sx={{ height: "22px", px: "12px", borderRadius: "100px", background: "#e61d50", display: "flex", alignItems: "center" }}>
          <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "10px", fontWeight: 600, color: "#fff", whiteSpace: "nowrap" }}>Upload</Typography>
        </Box>
        <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "9px", color: "rgba(255,255,255,0.4)" }}>or</Typography>
        <Box sx={{ flex: 1, maxWidth: "150px", height: "22px", borderRadius: "100px", border: "1px solid rgba(255,255,255,0.14)", display: "flex", alignItems: "center", pl: "8px", pr: "2px", justifyContent: "space-between" }}>
          <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "9px", color: "rgba(255,255,255,0.3)", whiteSpace: "nowrap", overflow: "hidden" }}>Paste a link</Typography>
          <Box sx={{ height: "16px", px: "8px", borderRadius: "100px", background: "#e61d50", display: "flex", alignItems: "center", flexShrink: 0 }}>
            <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "8px", fontWeight: 600, color: "#fff", whiteSpace: "nowrap" }}>Get Clips</Typography>
          </Box>
        </Box>
      </Box>

      {/* the file being dragged in (from bottom-right) and dropped — faster now */}
      <Box
        sx={{
          position: "absolute",
          width: "40px",
          height: "40px",
          left: "50%",
          top: "44%",
          ml: "-20px",
          mt: "-20px",
          zIndex: 3,
          animation: `fileDrop ${CYCLE} ease-in-out infinite`,
          "@keyframes fileDrop": {
            "0%": { opacity: 0, transform: "translate(150px, 96px) scale(0.85)" },
            "3%": { opacity: 1, transform: "translate(150px, 96px) scale(0.85)" },
            "11%": { opacity: 1, transform: "translate(0, 0) scale(1)" },
            "13%": { opacity: 1, transform: "translate(0, -4px) scale(1)" },
            "16%": { opacity: 1, transform: "translate(0, 0) scale(0.9)" },
            "20%": { opacity: 0, transform: "translate(0, 6px) scale(0.5)" },
            "100%": { opacity: 0, transform: "translate(0, 6px) scale(0.5)" },
          },
        }}
      >
        <img src={movFile} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
      </Box>
    </Box>

    {/* ── Processing bar — fills fully BEFORE any clips generate ── */}
    <Box sx={{ height: "4px", borderRadius: "3px", background: "rgba(255,255,255,0.07)", overflow: "hidden", my: "10px" }}>
      <Box
        sx={{
          height: "100%", borderRadius: "3px", background: "#e61d50",
          animation: `fill ${CYCLE} ease-in-out infinite`,
          "@keyframes fill": {
            "0%, 20%": { width: "0%" },
            "60%": { width: "100%" }, // grows red while the clips generate
            "96%": { width: "100%" },
            "100%": { width: "0%" },
          },
        }}
      />
    </Box>

    {/* ── List expands, clips generate, get clicked + exported ──── */}
    <Box
      sx={{
        overflow: "hidden",
        animation: `expand ${CYCLE} ease-in-out infinite`,
        "@keyframes expand": {
          "0%, 22%": { maxHeight: "0px", opacity: 0 },
          "28%": { maxHeight: "420px", opacity: 1 }, // opens as the bar starts, so clips fill in
          "96%": { maxHeight: "420px", opacity: 1 },  // clips stay on screen
          "99%, 100%": { maxHeight: "0px", opacity: 0 },
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "9px", px: "2px", pb: "6px", borderBottom: "1px solid rgba(255,255,255,0.06)", mb: "7px" }}>
        <Box sx={{ width: "15px", flexShrink: 0 }} /> {/* checkbox column spacer */}
        <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "9.5px", color: "#6c727d", width: { xs: "76px", md: "96px" }, flexShrink: 0 }}>CherryClipz</Typography>
        <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "9.5px", color: "#6c727d", width: "18px", textAlign: "center", flexShrink: 0 }}>Emo</Typography>
        <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "9.5px", color: "#6c727d" }}>Transcript</Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: "5px", md: "6px" } }}>
        {ROWS.map((row, i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "9px",
              borderRadius: "6px",
              borderLeft: "3px solid transparent",
              pl: "5px",
              py: "3px",
              opacity: 0,
              transformOrigin: "top center",
              animation: row.selectAt
                ? `rowIn${i} ${CYCLE} ease-in-out infinite, rowSel${i} ${CYCLE} ease-in-out infinite`
                : `rowIn${i} ${CYCLE} ease-in-out infinite`,
              [`@keyframes rowIn${i}`]: rowKeyframes(APPEAR[i]),
              ...(row.selectAt
                ? {
                    [`@keyframes rowSel${i}`]: selectKeyframes(row.selectAt),
                    [`@keyframes chkBox${i}`]: checkBoxKeyframes(row.selectAt),
                    [`@keyframes chkMark${i}`]: checkMarkKeyframes(row.selectAt),
                  }
                : {}),
            }}
          >
            {/* checkbox — ticks on when the cursor clicks this clip (like the ET) */}
            <Box
              sx={{
                width: "15px", height: "15px", borderRadius: "4px", flexShrink: 0,
                border: "1.5px solid rgba(255,255,255,0.3)",
                display: "flex", alignItems: "center", justifyContent: "center",
                animation: `chkBox${i} ${CYCLE} ease-in-out infinite`,
              }}
            >
              <Box
                component="span"
                sx={{
                  color: "#fff", fontSize: "10px", fontWeight: 900, lineHeight: 1, opacity: 0,
                  animation: `chkMark${i} ${CYCLE} ease-in-out infinite`,
                }}
              >
                ✓
              </Box>
            </Box>
            <ThumbScene base={row.base as [string, string]} />
            <Box sx={{ width: "18px", textAlign: "center", flexShrink: 0, color: EMO_COLOR[row.emo], fontWeight: 800, fontSize: "14px", fontFamily: "Outfit, sans-serif" }}>
              {row.emo}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: { xs: "11px", md: "12.5px" }, color: "#e8eaf3", lineHeight: 1.3 }}>
                {row.text}
              </Typography>
              <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "9px", color: "#7c8398", mt: "1px" }}>
                {row.speaker}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Export button — visible as a target, gets pressed when the cursor clicks it */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: "10px" }}>
        <Box
          sx={{
            height: "28px",
            px: "18px",
            borderRadius: "100px",
            background: "#e61d50",
            display: "flex",
            alignItems: "center",
            opacity: 0,
            animation: `exportBtn ${CYCLE} ease-in-out infinite`,
            "@keyframes exportBtn": {
              "0%, 60%": { opacity: 0, transform: "scale(0.9)" },
              "64%": { opacity: 1, transform: "scale(1)" },
              "81%": { opacity: 1, transform: "scale(1)", boxShadow: "0 0 0 0 rgba(230,29,80,0)" },
              "82.5%": { opacity: 1, transform: "scale(0.92)", boxShadow: "0 0 0 6px rgba(230,29,80,0.2)" }, // the click
              "84.5%": { opacity: 1, transform: "scale(1)", boxShadow: "0 0 0 0 rgba(230,29,80,0)" },
              "96%": { opacity: 1, transform: "scale(1)" },
              "100%": { opacity: 0, transform: "scale(1)" },
            },
          }}
        >
          <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "12px", fontWeight: 700, color: "#fff", whiteSpace: "nowrap" }}>
            Export →
          </Typography>
        </Box>
      </Box>
    </Box>

    {/* ── One cursor for the whole story: drag the file, then click clips + Export ── */}
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "22px",
        height: "22px",
        zIndex: 6,
        pointerEvents: "none",
        filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.55))",
        animation: `cursorPath ${CYCLE} ease-in-out infinite`,
        "@keyframes cursorPath": {
          // drag the file in from bottom-right (fast), release on drop
          "0%": { opacity: 0, transform: "translate(300px, 360px) scale(1)" },
          "3%": { opacity: 1, transform: "translate(300px, 360px) scale(1)" },
          "11%": { opacity: 1, transform: "translate(224px, 58px) scale(1)" },
          "15%": { opacity: 1, transform: "translate(224px, 58px) scale(1)" },
          "18%": { opacity: 1, transform: "translate(224px, 74px) scale(1)" },
          "21%": { opacity: 0, transform: "translate(224px, 82px) scale(1)" },
          // hidden while the bar fills and ALL 3 clips generate
          "63%": { opacity: 0, transform: "translate(96px, 200px) scale(1)" },
          "65%": { opacity: 1, transform: "translate(96px, 200px) scale(1)" },
          // click clip 1 (highlights at 68%)
          "67%": { opacity: 1, transform: "translate(96px, 200px) scale(1)" },
          "68%": { opacity: 1, transform: "translate(96px, 200px) scale(0.82)" },
          "69%": { opacity: 1, transform: "translate(96px, 200px) scale(1)" },
          // clip 2 (highlights at 72%)
          "71%": { opacity: 1, transform: "translate(96px, 266px) scale(1)" },
          "72%": { opacity: 1, transform: "translate(96px, 266px) scale(0.82)" },
          "73%": { opacity: 1, transform: "translate(96px, 266px) scale(1)" },
          // clip 3 (highlights at 76%)
          "75%": { opacity: 1, transform: "translate(96px, 332px) scale(1)" },
          "76%": { opacity: 1, transform: "translate(96px, 332px) scale(0.82)" },
          "77%": { opacity: 1, transform: "translate(96px, 332px) scale(1)" },
          // glide to Export and click (press at 82.5%)
          "81%": { opacity: 1, transform: "translate(372px, 372px) scale(1)" },
          "82.5%": { opacity: 1, transform: "translate(372px, 372px) scale(0.82)" },
          "84%": { opacity: 1, transform: "translate(372px, 372px) scale(1)" },
          // linger, then fade for the reset
          "96%": { opacity: 1, transform: "translate(372px, 372px) scale(1)" },
          "98%": { opacity: 0, transform: "translate(372px, 372px) scale(1)" },
          "100%": { opacity: 0, transform: "translate(372px, 372px) scale(1)" },
        },
      }}
    >
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
        <path
          d="M5.5 2.6 L5.5 17.8 L9.3 14.1 L11.8 19.8 L14.1 18.8 L11.6 13.2 L16.8 13.2 Z"
          fill="#ffffff" stroke="#0e0e12" strokeWidth="1.3" strokeLinejoin="round"
        />
      </svg>
    </Box>
  </Box>
);

export default HeroClipDemo;
